/**
 * Client-side mirror of the 400s that PUT /curation/curated-collection returns,
 * so the admin is told what is wrong before the round trip rather than after it.
 *
 * Kept as a pure function (no React, no store) so the rules can be unit tested
 * on their own and stay in one place if a second curated rail is ever added.
 */

export const MAX_SERIES = 10;
export const MIN_ACTIVE_SERIES = 5;
export const NAME_MAX_LENGTH = 100;

/**
 * @param {{ name?: string, isActive?: boolean, seriesIds?: number[] }} config
 * @returns {string[]} human-readable problems, empty when the config is saveable.
 *   The first entry is the one worth surfacing as a toast.
 */
export const validateCuratedCollection = ({ name = '', isActive = false, seriesIds = [] } = {}) => {
    const errors = [];
    const trimmedName = String(name).trim();
    const ids = Array.isArray(seriesIds) ? seriesIds : [];

    if (!trimmedName) {
        errors.push('Name is missing');
    } else if (trimmedName.length > NAME_MAX_LENGTH) {
        errors.push(`The name must be ${NAME_MAX_LENGTH} characters or fewer.`);
    }

    if (ids.length > MAX_SERIES) {
        errors.push(`Remove ${ids.length - MAX_SERIES} series — the limit is ${MAX_SERIES}.`);
    }

    if (new Set(ids).size !== ids.length) {
        errors.push('The list contains the same series twice.');
    }

    // A rail that is switched on must carry a full-looking row; below the floor
    // the app hides it entirely, so saving that state is almost always a mistake.
    if (isActive && ids.length < MIN_ACTIVE_SERIES) {
        errors.push(
            `An active collection needs at least ${MIN_ACTIVE_SERIES} series — ${ids.length} selected.`,
        );
    }

    return errors;
};
